import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { getSeguimientoStatus, getWeeklyStreak } from '../utils/api';
import SeguimientoDrawer from '../components/SeguimientoDrawer';

const MONTHS = ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'];

const pctColor = (p) => p >= 80 ? '#10B981' : p >= 40 ? '#F59E0B' : '#EF4444';
const pctBg   = (p) => p >= 80 ? 'rgba(16,185,129,0.1)' : p >= 40 ? 'rgba(245,158,11,0.1)' : 'rgba(239,68,68,0.1)';

const fmtRange = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const fri = new Date(d); fri.setDate(d.getDate() + 6);
  return `${d.getDate()} ${MONTHS[d.getMonth()]} — ${fri.getDate()} ${MONTHS[fri.getMonth()]}`;
};
const fmtDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return `${d.getDate()} ${MONTHS[d.getMonth()]}`;
};
const getWeekStart = (offset = 0) => {
  const today = new Date();
  const dow = today.getDay();
  const daysSinceMon = (dow + 6) % 7;
  const mon = new Date(today);
  mon.setDate(today.getDate() - daysSinceMon - offset * 7);
  mon.setHours(0, 0, 0, 0);
  return mon.toISOString().split('T')[0];
};

// ─── Fila de tarea — abre drawer al hacer click ───────────────────────────────
const TaskRow = ({ item, onOpen }) => {
  const pct   = item.has_update ? (item.update?.cumplimiento_pct ?? item.task.cumplimiento_pct ?? 0) : (item.task.cumplimiento_pct ?? 0);
  const color = pctColor(pct);
  const preview = item.update?.seguimiento || item.update?.resultado || null;

  return (
    <motion.button whileTap={{ scale: 0.985 }}
      onClick={() => onOpen(item)}
      className="w-full text-left rounded-2xl px-4 py-3.5 flex items-center gap-3"
      style={{
        background: item.has_update ? 'rgba(16,185,129,0.03)' : '#fff',
        border: `1.5px solid ${item.has_update ? 'rgba(16,185,129,0.15)' : 'rgba(0,0,0,0.07)'}`,
      }}>
      {/* % badge */}
      <div className="flex-shrink-0 w-10 h-10 rounded-2xl flex items-center justify-center"
        style={{ background: pct === 0 ? 'rgba(0,0,0,0.04)' : pctBg(pct) }}>
        {item.has_update
          ? <span className="text-[11px] font-black" style={{ color }}>{pct}%</span>
          : <span className="text-[11px] font-black" style={{ color: '#94A3B8' }}>{pct}%</span>
        }
      </div>
      {/* Info */}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold leading-snug" style={{ color: '#0F172A' }}>
          {item.task.title}
        </p>
        {preview ? (
          <p className="text-xs mt-0.5 truncate" style={{ color: '#64748B' }}>{preview}</p>
        ) : (
          <p className="text-xs mt-0.5 italic" style={{ color: '#CBD5E1' }}>Sin seguimiento esta semana</p>
        )}
      </div>
      {/* Check / chevron */}
      <div className="flex items-center gap-1.5 flex-shrink-0">
        {item.has_update && (
          <div className="w-5 h-5 rounded-full flex items-center justify-center"
            style={{ background: 'rgba(16,185,129,0.12)' }}>
            <svg className="w-3 h-3" style={{ color: '#10B981' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7"/>
            </svg>
          </div>
        )}
        <svg className="w-4 h-4" style={{ color: '#E2E8F0' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7"/>
        </svg>
      </div>
    </motion.button>
  );
};

// ─── Grupo por responsabilidad ────────────────────────────────────────────────
const RespGroup = ({ name, items, closedItems = [], onOpen, defaultOpen }) => {
  const [collapsed, setCollapsed] = useState(!defaultOpen);
  const done  = items.filter(i => i.has_update).length;
  const total = items.length;
  const pct   = total ? Math.round((done / total) * 100) : 0;
  const pc    = pct === 100 ? '#10B981' : pct >= 50 ? '#F59E0B' : '#EF4444';

  return (
    <div className="rounded-2xl overflow-hidden"
      style={{ background: '#fff', border: '1px solid rgba(0,0,0,0.08)',
        boxShadow: collapsed ? 'none' : '0 1px 6px rgba(0,0,0,0.05)' }}>
      <button className="w-full px-4 py-3.5 flex items-center gap-3 text-left"
        onClick={() => setCollapsed(c => !c)}>
        <svg className={`w-4 h-4 flex-shrink-0 transition-transform ${collapsed ? '' : 'rotate-180'}`}
          style={{ color: '#CBD5E1' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7"/>
        </svg>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] font-black uppercase tracking-widest" style={{ color: '#94A3B8' }}>Responsabilidad</p>
          <p className="text-[15px] font-bold leading-snug" style={{ color: '#0F172A' }}>{name}</p>
        </div>
        <div className="flex items-center gap-2.5 flex-shrink-0">
          <span className="text-xs font-black" style={{ color: pc }}>{done}/{total}</span>
          <div className="w-14 rounded-full overflow-hidden" style={{ height: 3, background: 'rgba(0,0,0,0.06)' }}>
            <div style={{ width: `${pct}%`, height: '100%', background: pc }} />
          </div>
        </div>
      </button>
      <AnimatePresence initial={false}>
        {!collapsed && (
          <motion.div initial={{ height: 0 }} animate={{ height: 'auto' }} exit={{ height: 0 }} className="overflow-hidden">
            <div className="px-3 pb-3" style={{ borderTop: '1px solid rgba(0,0,0,0.05)' }}>
              <div className="pt-2 space-y-2">
                {items.map(item => (
                  <TaskRow key={item.task.id} item={item} onOpen={onOpen} />
                ))}
                {closedItems.length > 0 && (
                  <div className="opacity-40 space-y-1 pt-1">
                    {closedItems.map(item => (
                      <div key={item.task.id} className="flex items-center gap-2.5 px-3 py-2 rounded-xl"
                        style={{ background: 'rgba(0,0,0,0.02)' }}>
                        <span>{item.task.status === 'Aprobada / Cerrada' ? '✅' : '❌'}</span>
                        <p className="text-sm flex-1 truncate"
                          style={{ color: '#64748B', textDecoration: item.task.status === 'Aprobada / Cerrada' ? 'line-through' : 'none' }}>
                          {item.task.title}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ─── Vista personal ───────────────────────────────────────────────────────────
const MiSemanaPersonal = () => {
  const [weekOffset, setWeekOffset] = useState(0);
  const [data, setData]             = useState(null);
  const [loading, setLoading]       = useState(true);
  const [streak, setStreak]         = useState(null);
  const [activeItem, setActiveItem] = useState(null);

  const isCurrent = weekOffset === 0;

  useEffect(() => { loadData(); }, [weekOffset]);

  const loadData = async () => {
    setLoading(true);
    try {
      const ws = getWeekStart(weekOffset);
      const [res, streakRes] = await Promise.all([
        getSeguimientoStatus(ws, true),
        isCurrent ? getWeeklyStreak().catch(() => null) : Promise.resolve(null),
      ]);
      setData(res.data);
      if (streakRes) setStreak(streakRes.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const items = data?.items || [];
  const done  = items.filter(i => i.has_update).length;
  const pct   = items.length ? Math.round((done / items.length) * 100) : 0;
  const pc    = done === 0 ? '#94A3B8' : pctColor(pct);

  const groups = {};
  items.forEach(item => {
    const key = item.task.responsabilidad || 'Sin responsabilidad';
    if (!groups[key]) groups[key] = { active: [], closed: [] };
    groups[key].active.push(item);
  });
  (data?.completed_items || []).forEach(item => {
    const key = item.task.responsabilidad || 'Sin responsabilidad';
    if (!groups[key]) groups[key] = { active: [], closed: [] };
    groups[key].closed.push(item);
  });
  const groupKeys = Object.keys(groups).sort();

  return (
    <div style={{ minHeight: '100vh', background: '#F1F5F9' }}>
      <div className="sticky top-14 md:top-0 z-20"
        style={{ background: 'rgba(10,26,20,0.94)', backdropFilter: 'blur(20px)',
          borderBottom: '1.5px solid rgba(0,208,132,0.25)' }}>
        <div className="max-w-2xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <button onClick={() => setWeekOffset(o => o + 1)}
              className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{ background: 'rgba(255,255,255,0.08)', color: '#6EE7B7' }}>
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7"/>
              </svg>
            </button>
            <div className="flex-1 text-center">
              <p className="text-lg font-black leading-none"
                style={{ background: 'linear-gradient(135deg,#00D084,#00FFB3)',
                  WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                Mi Semana
              </p>
              <p className="text-xs font-bold mt-0.5" style={{ color: '#6EE7B7' }}>
                {isCurrent ? 'Semana actual' : weekOffset === 1 ? 'Sem. anterior' : `Hace ${weekOffset} sem.`}
                {' · '}{fmtRange(data?.week_start)}
              </p>
            </div>
            {weekOffset > 0 ? (
              <button onClick={() => setWeekOffset(o => Math.max(0, o - 1))}
                className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ background: 'rgba(255,255,255,0.08)', color: '#6EE7B7' }}>
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7"/>
                </svg>
              </button>
            ) : (
              <button onClick={loadData}
                className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ background: 'rgba(255,255,255,0.08)', color: '#6EE7B7' }}>
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
                </svg>
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-4 space-y-3">
        {!loading && data && (
          <div className="rounded-2xl px-4 py-3.5 flex items-center gap-4"
            style={{ background: '#fff', border: '1px solid rgba(0,0,0,0.07)' }}>
            <span className="text-2xl font-black" style={{ color: pc }}>{pct}%</span>
            <div className="flex-1">
              <div className="rounded-full overflow-hidden mb-1" style={{ height: 5, background: 'rgba(0,0,0,0.06)' }}>
                <motion.div initial={{ width: 0 }} animate={{ width: `${pct}%` }}
                  transition={{ duration: 0.9, ease: 'easeOut' }}
                  style={{ height: '100%', background: pc, borderRadius: 9999 }} />
              </div>
              <p className="text-xs" style={{ color: '#94A3B8' }}>
                {done}/{items.length} seguimientos completados
                {streak?.streak > 0 && (
                  <span className="ml-2 font-bold" style={{ color: '#F59E0B' }}>🔥 {streak.streak} sem.</span>
                )}
              </p>
            </div>
            {done === items.length && items.length > 0 && (
              <span className="text-xs font-bold px-2.5 py-1 rounded-xl flex-shrink-0"
                style={{ background: 'rgba(16,185,129,0.1)', color: '#10B981' }}>
                ✓ Completo
              </span>
            )}
          </div>
        )}

        {loading && (
          <div className="flex justify-center py-20">
            <div className="w-6 h-6 border-2 rounded-full animate-spin"
              style={{ borderColor: '#00C878', borderTopColor: 'transparent' }} />
          </div>
        )}

        {!loading && groupKeys.map((key, idx) => (
          <RespGroup key={key} name={key}
            items={groups[key].active}
            closedItems={groups[key].closed}
            onOpen={setActiveItem}
            defaultOpen={idx === 0}
          />
        ))}

        <div style={{ height: 40 }} />
      </div>

      <AnimatePresence>
        {activeItem && (
          <SeguimientoDrawer
            item={activeItem}
            weekOffset={weekOffset}
            onClose={() => setActiveItem(null)}
            onSaved={() => { setActiveItem(null); loadData(); }}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

// ─── Vista gerencial ──────────────────────────────────────────────────────────
const MiSemanaEquipo = () => {
  const [items, setItems]         = useState([]);
  const [weekStart, setWeekStart] = useState('');
  const [loading, setLoading]     = useState(true);
  const [collapsed, setCollapsed] = useState({});

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await getSeguimientoStatus();
      setItems(res.data.items || []);
      setWeekStart(res.data.week_start || '');
    } catch {}
    setLoading(false);
  };

  const toggle = (key) => setCollapsed(c => ({ ...c, [key]: !c[key] }));

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
    </div>
  );

  const personMap = {};
  items.forEach(item => {
    const uid = item.task.responsible_user_id || '__';
    if (!personMap[uid]) personMap[uid] = { id: uid, name: item.responsible_name, dept: item.department_name, groups: {}, total: 0, updated: 0 };
    const resp = item.task.responsabilidad || 'Sin responsabilidad';
    if (!personMap[uid].groups[resp]) personMap[uid].groups[resp] = [];
    personMap[uid].groups[resp].push(item);
    personMap[uid].total++;
    if (item.has_update) personMap[uid].updated++;
  });

  const deptMap = {};
  Object.values(personMap).forEach(p => {
    const dept = p.dept || 'Sin departamento';
    if (!deptMap[dept]) deptMap[dept] = { name: dept, persons: [], total: 0, updated: 0 };
    deptMap[dept].persons.push(p);
    deptMap[dept].total += p.total;
    deptMap[dept].updated += p.updated;
  });

  const departments = Object.values(deptMap).sort((a, b) =>
    (a.total ? a.updated / a.total : 1) - (b.total ? b.updated / b.total : 1)
  );

  const totalUpdates = items.filter(i => i.has_update).length;
  const globalPct    = items.length ? Math.round((totalUpdates / items.length) * 100) : 0;
  const globalPc     = pctColor(globalPct);

  return (
    <div className="p-4 md:p-6 max-w-3xl mx-auto space-y-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-foreground">Mi Semana — Equipo</h1>
          <p className="text-sm text-muted-foreground mt-0.5">{fmtRange(weekStart)}</p>
        </div>
        <button onClick={loadData} className="text-xs text-muted-foreground hover:text-foreground mt-1">↻</button>
      </div>
      <div className="decko-card px-5 py-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Actualizaciones recibidas</span>
          <span className="text-sm font-black" style={{ color: globalPc }}>{totalUpdates}/{items.length}</span>
        </div>
        <div className="w-full bg-secondary rounded-full h-1.5 overflow-hidden">
          <motion.div initial={{ width: 0 }} animate={{ width: `${globalPct}%` }} transition={{ duration: 0.8 }}
            className="h-full rounded-full" style={{ background: globalPc }} />
        </div>
      </div>
      <div className="space-y-3">
        {departments.map(dept => {
          const dp = dept.total ? Math.round((dept.updated / dept.total) * 100) : 0;
          const dc = pctColor(dp);
          const dKey = `dept-${dept.name}`;
          return (
            <div key={dept.name} className="decko-card overflow-hidden">
              <button className="w-full px-5 py-3.5 flex items-center gap-3 hover:bg-white/[0.02] transition-colors text-left"
                onClick={() => toggle(dKey)}>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-bold uppercase tracking-widest mb-0.5" style={{ color: '#94A3B8' }}>Departamento</p>
                  <h3 className="font-bold text-foreground text-sm">{dept.name}</h3>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className="text-xs font-black" style={{ color: dc }}>{dp}%</span>
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${dept.updated === dept.total ? 'bg-success/10 text-success' : dept.updated === 0 ? 'bg-secondary text-muted-foreground' : 'bg-warning/10 text-warning'}`}>
                    {dept.updated}/{dept.total}
                  </span>
                  <svg className={`w-4 h-4 text-muted-foreground transition-transform ${collapsed[dKey] ? '' : 'rotate-180'}`}
                    fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7"/>
                  </svg>
                </div>
              </button>
              <AnimatePresence initial={false}>
                {!collapsed[dKey] && (
                  <motion.div initial={{ height: 0 }} animate={{ height: 'auto' }} exit={{ height: 0 }} className="overflow-hidden">
                    <div className="border-t border-border">
                      {dept.persons.map(person => {
                        const pKey = `person-${person.id}`;
                        return (
                          <div key={person.id} className="border-b border-border/50 last:border-b-0">
                            <button className="w-full px-5 py-2.5 flex items-center gap-3 hover:bg-white/[0.03] text-left"
                              onClick={() => toggle(pKey)}>
                              <div className="w-7 h-7 rounded-lg flex items-center justify-center text-white font-black text-xs flex-shrink-0"
                                style={{ background: 'linear-gradient(135deg,#6366F1,#818CF8)' }}>
                                {person.name.charAt(0).toUpperCase()}
                              </div>
                              <span className="font-semibold text-foreground text-sm flex-1">{person.name}</span>
                              <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${person.updated === person.total ? 'bg-success/10 text-success' : 'bg-warning/10 text-warning'}`}>
                                {person.updated}/{person.total}
                              </span>
                              <svg className={`w-3.5 h-3.5 text-muted-foreground transition-transform ${collapsed[pKey] ? '' : 'rotate-180'}`}
                                fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7"/>
                              </svg>
                            </button>
                            <AnimatePresence initial={false}>
                              {!collapsed[pKey] && (
                                <motion.div initial={{ height: 0 }} animate={{ height: 'auto' }} exit={{ height: 0 }} className="overflow-hidden">
                                  <div className="pl-5 pr-5 pb-3 pt-2 space-y-3">
                                    {Object.entries(person.groups).map(([resp, tasks]) => (
                                      <div key={resp}>
                                        <p className="text-xs font-bold uppercase tracking-widest mb-1.5" style={{ color: '#94A3B8' }}>{resp}</p>
                                        <div className="space-y-2 pl-2">
                                          {tasks.map(item => {
                                            const tp = item.update?.cumplimiento_pct ?? 0;
                                            return (
                                              <div key={item.task.id} className="rounded-xl p-3"
                                                style={{ background: item.has_update ? 'rgba(16,185,129,0.04)' : 'rgba(245,158,11,0.04)', border: `1px solid ${item.has_update ? 'rgba(16,185,129,0.12)' : 'rgba(245,158,11,0.12)'}` }}>
                                                <div className="flex items-start gap-2">
                                                  <div className="w-1.5 h-1.5 rounded-full flex-shrink-0 mt-1.5"
                                                    style={{ background: item.has_update ? '#10B981' : '#F59E0B' }} />
                                                  <div className="flex-1 min-w-0">
                                                    <p className="text-sm font-medium text-foreground">{item.task.title}</p>
                                                    {item.has_update ? (
                                                      <p className="text-xs text-muted-foreground mt-0.5">{item.update.seguimiento || item.update.resultado}</p>
                                                    ) : (
                                                      <p className="text-xs italic mt-0.5" style={{ color: '#CBD5E1' }}>Sin seguimiento</p>
                                                    )}
                                                  </div>
                                                  {item.has_update && tp > 0 && (
                                                    <span className="text-sm font-black flex-shrink-0" style={{ color: pctColor(tp) }}>{tp}%</span>
                                                  )}
                                                </div>
                                              </div>
                                            );
                                          })}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </motion.div>
                              )}
                            </AnimatePresence>
                          </div>
                        );
                      })}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const MiSemana = () => {
  const { user } = useAuth();
  if (user?.role === 'Presidencia' || user?.role === 'Gerencia') return <MiSemanaEquipo />;
  return <MiSemanaPersonal />;
};

export default MiSemana;
